/**
 * Comparatorul de curs — `GET /api/valutar/:data` si scrierile de pe el.
 *
 * Regula care conduce tot ecranul: **NULL se propaga**. Un curs necompletat e `null`, niciodata 0,
 * pentru ca o banca fara cotatie ar castiga orice comparatie — cel mai periculos esec silentios din
 * tot setul de feature-uri. Consecinta directa in stratul asta: **actualizarea optimista scrie
 * cursul si golește cifrele derivate**. Valoarea in lei, comisionul si costul total depind de tarif,
 * de praguri semi-deschise si de moneda comisionului; clientul nu le poate recalcula corect, iar a
 * lasa vizibile cifrele VECHI langa un curs NOU ar fi exact felul de „aproape corect" pe care
 * aplicatia il elimina. Deci pana raspunde serverul se afiseaza `—`, adica „necunoscut".
 *
 * Al doilea invariant: castigatorul NU e fill-ul verde din fisier (acela marca celula-ANCORA de
 * curs). Serverul il recalculeaza la fiecare scriere si il intoarce in `winnerBankId`; clientul nu
 * il deduce niciodata singur.
 */

import { useMemo } from 'react';
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { BankTariff, BankTariffSurcharge } from '@evomag/core/tariff';

import { api } from '../lib/api.ts';
import { esteConflict } from '../lib/api-errors.ts';
import { chei } from '../lib/query-keys.ts';
import {
  derivaStareCelula,
  magazinEditari,
  useEditareLocala,
  type StareSalvare,
} from './editari.ts';
import type {
  BulkFxQuotesInput,
  FxQuoteDto,
  FxQuotePatchResponse,
  FxWinnerOverrideInput,
  PatchFxQuoteInput,
} from '../../../../functions/api/schemas.ts';

export type { FxQuoteDto, FxQuotePatchResponse };

export interface BancaValutar {
  id: number;
  code: string;
  name: string;
  isReference: boolean;
  columnOrder: number | null;
  portalUrl: string | null;
  tariffs: BankTariff[];
}

export interface EconomieTranzactie {
  winnerBankId: number | null;
  winnerRateE6: number | null;
  winnerValueMinor: number | null;
  winnerCommissionMinor: number | null;
  winnerCostMinor: number | null;
  bnrRateE6: number | null;
  bnrValueMinor: number | null;
  bnrCostMinor: number | null;
  savingMinor: number | null;
  /** Recalculat corect: sursa scadea celula de CURS in loc de cea de COMISION (~3,7 lei eroare). */
  bnrVsWinnerMinor: number | null;
  legacySavingReal: number | null;
  legacyBnrVsWinnerReal: number | null;
  pendingQuoteCount: number;
}

export interface TranzactieValutara {
  id: number;
  rowSeq: number;
  supplierId: number | null;
  supplierName: string | null;
  supplierUnresolved: boolean;
  counterpartyRaw: string;
  currency: string;
  currencyRaw: string | null;
  amountMinor: number;
  amountExpression: string | null;
  amountEurEquivMinor: number | null;
  destinationCountry: string | null;
  isExpress: boolean;
  state: string;
  rawFillArgb: string | null;
  note: string | null;
  version: number;
  lineCount: number;
  invoiceRefCount: number;
  commentCount: number;
  attachmentCount: number;
  quotes: FxQuoteDto[];
  saving: EconomieTranzactie | null;
}

export interface BlocValutar {
  id: number;
  businessDate: string;
  seq: number;
  labelRaw: string;
  operatorLabel: string | null;
  runId: number | null;
  currencyPrimary: string;
  currencyPrimaryRaw: string | null;
  isClosed: boolean;
  note: string | null;
  version: number;
  totals: {
    totalSavingMinor: number | null;
    totalBnrVsWinnerMinor: number | null;
    totalAmountMinor: number | null;
    txnCount: number;
    unpricedCount: number;
    /** `N cursuri lipsă în acest bloc` — o celula goala intr-un SUM() e greseala facuta imposibila. */
    pendingQuoteCount: number;
    legacyTotalSavingReal: number | null;
    legacyBnrVsWinnerTotalReal: number | null;
  };
  transactions: TranzactieValutara[];
}

export interface RaspunsValutar {
  date: string;
  banks: BancaValutar[];
  /** Cursurile BNR ale zilei, `{ EUR: 5_240_900 }` — intregi x10^6, ca peste tot. */
  referenceRates: Record<string, number>;
  blocks: BlocValutar[];
}

export type SuprataxaBanca = BankTariffSurcharge;

export function useValutar(
  data: string,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsValutar, Error> {
  return useQuery({
    queryKey: chei.valutar.data(data),
    queryFn: ({ signal }) => api.get<RaspunsValutar>(`/valutar/${data}`, { semnal: signal }),
    enabled: optiuni?.activ !== false && data !== '',
  });
}

// ---------------------------------------------------------------------------
// Ajutoare de cache
// ---------------------------------------------------------------------------

function transformaCotatii(
  raspuns: RaspunsValutar,
  txnId: number,
  transforma: (cotatii: FxQuoteDto[]) => FxQuoteDto[],
  winnerBankId?: number | null,
): RaspunsValutar {
  return {
    ...raspuns,
    blocks: raspuns.blocks.map((bloc) => ({
      ...bloc,
      transactions: bloc.transactions.map((txn) =>
        txn.id === txnId
          ? {
              ...txn,
              quotes: transforma(txn.quotes),
              saving:
                txn.saving && winnerBankId !== undefined
                  ? { ...txn.saving, winnerBankId }
                  : txn.saving,
            }
          : txn,
      ),
    })),
  };
}

/** Gaseste tranzactia careia ii apartine o cotatie, fara ca apelantul sa o trimita explicit. */
export function tranzactiaCotatiei(
  raspuns: RaspunsValutar | undefined,
  quoteId: number,
): TranzactieValutara | undefined {
  for (const bloc of raspuns?.blocks ?? []) {
    const gasita = bloc.transactions.find((t) => t.quotes.some((q) => q.id === quoteId));
    if (gasita) return gasita;
  }
  return undefined;
}

/**
 * Scrierea optimista a unui curs.
 *
 * Se scrie cursul si se GOLESC cifrele derivate. Vezi comentariul din capul fisierului: valorile
 * vechi langa un curs nou ar minti mai convingator decat un `—`.
 */
export function aplicaCursOptimist(cotatie: FxQuoteDto, rateE6: number | null): FxQuoteDto {
  if (rateE6 === null) {
    return {
      ...cotatie,
      rateE6: null,
      rateSource: 'pending',
      rateDate: null,
      rateVsBnrPctE6: null,
      amountRonMinor: null,
      feeMinor: null,
      feeCurrency: null,
      feeRonMinor: null,
      totalCostRonMinor: null,
      isWinner: false,
      winnerSource: null,
    };
  }
  return {
    ...cotatie,
    rateE6,
    amountRonMinor: null,
    feeMinor: null,
    feeRonMinor: null,
    totalCostRonMinor: null,
  };
}

// ---------------------------------------------------------------------------
// PATCH /api/fx-quotes/:id
// ---------------------------------------------------------------------------

export interface VariabilePatchCotatie {
  quoteId: number;
  txnId: number;
  patch: PatchFxQuoteInput;
  /** Textul brut tastat in celula de curs (`5,2409`), pastrat pentru chipul `Nesalvat`. */
  brut?: string;
}

export function fnPatchCotatie(variabile: VariabilePatchCotatie): Promise<FxQuotePatchResponse> {
  return api.patch<FxQuotePatchResponse>(`/fx-quotes/${variabile.quoteId}`, variabile.patch);
}

interface ContextCotatie {
  anterior: RaspunsValutar | undefined;
}

export function optiuniPatchCotatie(queryClient: QueryClient, data: string) {
  return {
    mutationKey: chei.mutatii.patchCotatie(),
    mutationFn: fnPatchCotatie,

    onMutate: async (variabile: VariabilePatchCotatie): Promise<ContextCotatie> => {
      await queryClient.cancelQueries({ queryKey: chei.valutar.data(data) });
      const anterior = queryClient.getQueryData<RaspunsValutar>(chei.valutar.data(data));

      magazinEditari.curata('cotatie', variabile.quoteId, ['rateE6']);

      if (anterior) {
        queryClient.setQueryData<RaspunsValutar>(
          chei.valutar.data(data),
          transformaCotatii(anterior, variabile.txnId, (cotatii) =>
            cotatii.map((q) =>
              q.id === variabile.quoteId ? aplicaCursOptimist(q, variabile.patch.rateE6) : q,
            ),
          ),
        );
      }
      return { anterior };
    },

    onError: (
      eroare: Error,
      variabile: VariabilePatchCotatie,
      context: ContextCotatie | undefined,
    ): void => {
      if (context?.anterior) {
        queryClient.setQueryData(chei.valutar.data(data), context.anterior);
      }
      if (variabile.brut !== undefined) {
        magazinEditari.marcheaza({
          zona: 'cotatie',
          id: variabile.quoteId,
          camp: 'rateE6',
          valoare: variabile.brut,
          stare: esteConflict(eroare) ? 'conflict' : 'nesalvat',
          mesaj: eroare.message,
          la: Date.now(),
        });
      }
      if (esteConflict(eroare)) {
        void queryClient.invalidateQueries({ queryKey: chei.valutar.data(data) });
      }
    },

    onSuccess: (raspuns: FxQuotePatchResponse, variabile: VariabilePatchCotatie): void => {
      magazinEditari.curata('cotatie', variabile.quoteId, ['rateE6']);

      // Serverul intoarce TOATE cotatiile tranzactiei, pentru ca o scriere de curs poate schimba
      // castigatorul pe alt rand. Se inlocuieste setul intreg, nu doar celula editata.
      const curent = queryClient.getQueryData<RaspunsValutar>(chei.valutar.data(data));
      if (curent) {
        queryClient.setQueryData<RaspunsValutar>(
          chei.valutar.data(data),
          transformaCotatii(curent, variabile.txnId, () => raspuns.quotes, raspuns.winnerBankId),
        );
      }
      // Totalurile de bloc (`economie`, `cursuri lipsă`) vin din alt view: se cer din nou.
      void queryClient.invalidateQueries({ queryKey: chei.valutar.data(data) });
      void queryClient.invalidateQueries({ queryKey: chei.comisioane.tot() });
    },
  };
}

export function usePatchCotatie(
  data: string,
): UseMutationResult<FxQuotePatchResponse, Error, VariabilePatchCotatie, ContextCotatie> {
  const queryClient = useQueryClient();
  return useMutation(optiuniPatchCotatie(queryClient, data));
}

/** Starea de salvare a celulei de curs, aceeasi mecanica ca in grila zilei. */
export function useStareCotatie(quoteId: number): StareSalvare {
  const stari = useMutationState({
    filters: { mutationKey: chei.mutatii.patchCotatie(quoteId), exact: false },
    select: (mutatie) => ({
      status: mutatie.state.status,
      submittedAt: mutatie.state.submittedAt,
      variabile: mutatie.state.variables as VariabilePatchCotatie | undefined,
    }),
  });
  const editare = useEditareLocala('cotatie', quoteId, 'rateE6');
  const aleCelulei = useMemo(
    () =>
      stari
        .filter((s) => s.variabile?.quoteId === quoteId)
        .map((s) => ({ status: s.status, submittedAt: s.submittedAt })),
    [stari, quoteId],
  );
  return derivaStareCelula(aleCelulei, editare);
}

// ---------------------------------------------------------------------------
// POST /api/fx-quotes/bulk
// ---------------------------------------------------------------------------

export interface RaspunsCotatiiBulk {
  updated: number;
  failed: number;
  /** Per rand: un curs refuzat NU opreste restul coloanei lipite din clipboard. */
  results: Array<{
    quoteId: number;
    ok: boolean;
    result?: FxQuotePatchResponse;
    error?: string;
  }>;
}

export function fnCotatiiBulk(intrare: BulkFxQuotesInput): Promise<RaspunsCotatiiBulk> {
  return api.post<RaspunsCotatiiBulk>('/fx-quotes/bulk', intrare);
}

export function useCotatiiBulk(
  data: string,
): UseMutationResult<RaspunsCotatiiBulk, Error, BulkFxQuotesInput> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsCotatiiBulk, Error, BulkFxQuotesInput>({
    mutationKey: chei.mutatii.cotatiiBulk(),
    mutationFn: fnCotatiiBulk,
    // Fara actualizare optimista: 200 de cursuri lipite pot esua partial, iar un rollback „pe
    // jumatate" ar fi imposibil de citit. Se asteapta raspunsul si se reincarca blocul.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.valutar.data(data) });
      void queryClient.invalidateQueries({ queryKey: chei.comisioane.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/fx-quotes/:id/preia-bnr
// ---------------------------------------------------------------------------

export interface RaspunsPreiaBnr extends FxQuotePatchResponse {
  /** Vechimea e explicita: luni, pentru un bloc datat sambata, `daysBack = 2`. */
  rate: {
    rateE6: number;
    rateDate: string;
    isStale: boolean;
    daysBack: number;
    note: string | null;
  };
}

export function fnPreiaBnr(variabile: { quoteId: number }): Promise<RaspunsPreiaBnr> {
  return api.post<RaspunsPreiaBnr>(`/fx-quotes/${variabile.quoteId}/preia-bnr`);
}

export function usePreiaBnr(
  data: string,
): UseMutationResult<RaspunsPreiaBnr, Error, { quoteId: number }> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPreiaBnr, Error, { quoteId: number }>({
    mutationKey: chei.mutatii.preiaBnr(),
    mutationFn: fnPreiaBnr,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.valutar.data(data) });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/fx-transactions/:id/winner
// ---------------------------------------------------------------------------

export interface RaspunsCastigator {
  txnId: number;
  winnerBankId: number;
  reason: string;
  quotes: FxQuoteDto[];
  saving: unknown;
}

export interface VariabileCastigator {
  txnId: number;
  /** `reason` e OBLIGATORIU: cea mai ieftina banca nu e mereu cea posibila operational. */
  alegere: FxWinnerOverrideInput;
}

export function fnSetCastigator(variabile: VariabileCastigator): Promise<RaspunsCastigator> {
  return api.post<RaspunsCastigator>(`/fx-transactions/${variabile.txnId}/winner`, variabile.alegere);
}

export function useSetCastigator(
  data: string,
): UseMutationResult<RaspunsCastigator, Error, VariabileCastigator> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsCastigator, Error, VariabileCastigator>({
    mutationKey: chei.mutatii.setCastigator(),
    mutationFn: fnSetCastigator,
    onSuccess: (raspuns, variabile) => {
      const curent = queryClient.getQueryData<RaspunsValutar>(chei.valutar.data(data));
      if (curent) {
        queryClient.setQueryData<RaspunsValutar>(
          chei.valutar.data(data),
          transformaCotatii(curent, variabile.txnId, () => raspuns.quotes, raspuns.winnerBankId),
        );
      }
      void queryClient.invalidateQueries({ queryKey: chei.valutar.data(data) });
    },
  });
}
